import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';

import Bnr from './Components/Bnr';
import DynamicComponent from './Components/DynamicComponent';
import FacetedSearch from './Components/FacetedSearch';
import FacetedSearchCompactContainer from './Components/FacetedSearchCompactContainer';
import MiniCart from './Components/MiniCart';
import Navigation from './Components/Navigation';
import QuickSearch from './Components/QuickSearch';
import ShowMore from './Components/ShowMore';
import initializeKlaviyoSignForm from './Components/KlaviyoSignForm';
import SizeGuide from './Components/SizeGuide';
import SizeSelector from './Components/SizeSelector';
import { historyMiddleware } from './Middlewares/History.middleware';
import app, { createReducer } from './reducers';
import { monitor } from './Services/Cart.service';
import { installGlobalErrorReporting } from './Services/errorReporter';
import { initMyPageAddressAutocomplete } from './Services/googlePlacesAddress';
import { get } from './Services/http';
import RemoveButton from './Components/RemoveButton';
import AddToWishlistButton from './Components/AddToWishlistButton';

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(app, preloadState, composeWithDevTools(applyMiddleware(thunk, historyMiddleware)));

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
    if (!store.asyncReducers[key]) {
        store.asyncReducers[key] = asyncReducer;
        store.replaceReducer(createReducer(store.asyncReducers));
    }
};

window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
};

installGlobalErrorReporting();

const registeredContainers = [];
let monthlyChatBootstrapPromise = null;
let lazyImageObserver = null;
let lazyImageBootstrapped = false;
let nonCriticalBootstrapScheduled = false;
const renderReact = (element, container, callback) => {
    registeredContainers.push(container);
    ReactDOM.render(element, container, callback);
};

const ensureMonthlyChatContainer = () => {
    let container = document.getElementById('monthlyChat');
    if (container) {
        return container;
    }

    container = document.createElement('div');
    container.id = 'monthlyChat';
    document.body.appendChild(container);
    return container;
};

const removeMonthlyChatContainer = () => {
    const container = document.getElementById('monthlyChat');
    if (!container) {
        return;
    }

    try {
        ReactDOM.unmountComponentAtNode(container);
    } catch (error) {
        // no-op
    }

    if (container.parentNode) {
        container.parentNode.removeChild(container);
    }
};

const bootstrapMonthlyChat = () => {
    if (monthlyChatBootstrapPromise) {
        return monthlyChatBootstrapPromise;
    }

    monthlyChatBootstrapPromise = (async () => {
        try {
            const configResponse = await get('/api/monthly/chat/config');
            const config = await configResponse.json();

            if (!config?.isEnabled) {
                removeMonthlyChatContainer();
                return;
            }

            const monthlyChatContainer = ensureMonthlyChatContainer();
            const { default: MonthlyChat } = await import('./Components/MonthlyChat');
            renderReact(<MonthlyChat />, monthlyChatContainer);
        } catch (error) {
            // If config cannot be read, do not render chat button.
            removeMonthlyChatContainer();
        }
    })();

    return monthlyChatBootstrapPromise;
};

const scheduleWhenIdle = (callback, timeout = 1500) => {
    if (typeof callback !== 'function') {
        return;
    }

    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => callback(), { timeout });
        return;
    }

    window.setTimeout(callback, 0);
};

const lazyImageInitializedAttribute = 'data-lazy-placeholder-initialized';

const shouldForceNoLazyImage = (img) =>
    !!(
        img &&
        img.nodeName === 'IMG' &&
        (
            img.classList.contains('no-lazy') ||
            img.classList.contains('skip-lazy') ||
            img.classList.contains('navbar__link--icon') ||
            img.closest('[data-disable-lazy-image]')
        )
    );

const applyNoLazyImageSettings = (img) => {
    if (!shouldForceNoLazyImage(img)) {
        return false;
    }

    img.setAttribute(lazyImageInitializedAttribute, 'true');
    img.classList.remove('lazy-image', 'lazy-image--loading', 'lazy-image--loaded', 'lazy-image--error');

    if (!img.hasAttribute('loading') || img.getAttribute('loading') === 'lazy') {
        img.setAttribute('loading', 'eager');
    }

    if (!img.hasAttribute('decoding')) {
        img.setAttribute('decoding', 'async');
    }

    return true;
};

const shouldSkipLazyImageEnhancement = (img) => {
    if (!img || img.nodeName !== 'IMG' || img.getAttribute(lazyImageInitializedAttribute) === 'true') {
        return true;
    }

    const src = (img.getAttribute('src') || '').trim();
    const srcset = (img.getAttribute('srcset') || '').trim();
    if (!src && !srcset) {
        return true;
    }

    // Avoid tracking pixels and tiny placeholders.
    const width = parseInt(img.getAttribute('width') || '0', 10);
    const height = parseInt(img.getAttribute('height') || '0', 10);
    if ((width > 0 && width <= 2) || (height > 0 && height <= 2)) {
        return true;
    }

    if (/^data:image\//i.test(src) && src.length < 256) {
        return true;
    }

    return false;
};

const markLazyImageState = (img, isLoaded, isError) => {
    img.classList.remove('lazy-image--loading');

    if (isLoaded) {
        img.classList.remove('lazy-image--error');
        img.classList.add('lazy-image--loaded');
        return;
    }

    if (isError) {
        img.classList.remove('lazy-image--loaded');
        img.classList.add('lazy-image--error');
    }
};

const refreshLazyImageState = (img) => {
    if (!img || !img.classList.contains('lazy-image')) {
        return;
    }

    if (img.complete && img.naturalWidth > 0) {
        markLazyImageState(img, true, false);
        return;
    }

    img.classList.remove('lazy-image--loaded', 'lazy-image--error');
    img.classList.add('lazy-image--loading');

    img.addEventListener(
        'load',
        () => {
            markLazyImageState(img, true, false);
        },
        { once: true }
    );

    img.addEventListener(
        'error',
        () => {
            markLazyImageState(img, false, true);
        },
        { once: true }
    );
};

const initializeLazyImage = (img) => {
    if (applyNoLazyImageSettings(img)) {
        return;
    }

    if (shouldSkipLazyImageEnhancement(img)) {
        return;
    }

    img.setAttribute(lazyImageInitializedAttribute, 'true');
    img.classList.add('lazy-image');

    if (!img.hasAttribute('loading')) {
        img.setAttribute('loading', 'lazy');
    }

    if (!img.hasAttribute('decoding')) {
        img.setAttribute('decoding', 'async');
    }

    if (!img.style.aspectRatio) {
        const width = parseInt(img.getAttribute('width') || '0', 10);
        const height = parseInt(img.getAttribute('height') || '0', 10);
        if (width > 0 && height > 0) {
            img.style.aspectRatio = `${width} / ${height}`;
        }
    }

    refreshLazyImageState(img);
};

const initializeLazyImagesInNode = (rootNode) => {
    if (!rootNode || rootNode.nodeType !== Node.ELEMENT_NODE) {
        return;
    }

    if (rootNode.nodeName === 'IMG') {
        initializeLazyImage(rootNode);
    }

    const images = rootNode.querySelectorAll ? rootNode.querySelectorAll('img') : [];
    images.forEach((img) => initializeLazyImage(img));
};

const bootstrapLazyImagePlaceholders = () => {
    if (lazyImageBootstrapped) {
        return;
    }

    lazyImageBootstrapped = true;
    initializeLazyImagesInNode(document.body || document.documentElement);

    if (!window.MutationObserver || !document.body) {
        return;
    }

    if (lazyImageObserver) {
        lazyImageObserver.disconnect();
    }

    const pendingNodes = new Set();
    let flushScheduled = false;

    const flushPendingNodes = () => {
        flushScheduled = false;
        const nodes = Array.from(pendingNodes);
        pendingNodes.clear();
        nodes.forEach((node) => initializeLazyImagesInNode(node));
    };

    const scheduleFlush = () => {
        if (flushScheduled) {
            return;
        }

        flushScheduled = true;
        if (typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(flushPendingNodes);
            return;
        }

        window.setTimeout(flushPendingNodes, 0);
    };

    lazyImageObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (!node || node.nodeType !== Node.ELEMENT_NODE) {
                    return;
                }

                pendingNodes.add(node);
            });
        });

        if (pendingNodes.size > 0) {
            scheduleFlush();
        }
    });

    lazyImageObserver.observe(document.body, {
        childList: true,
        subtree: true,
    });
};

const bootstrapNonCriticalEnhancements = () => {
    if (nonCriticalBootstrapScheduled) {
        return;
    }

    nonCriticalBootstrapScheduled = true;
    scheduleWhenIdle(bootstrapMonthlyChat);
    scheduleWhenIdle(bootstrapLazyImagePlaceholders);
    scheduleWhenIdle(initializeKlaviyoSignForm);
    scheduleWhenIdle(initMyPageAddressAutocomplete);
};

window.onunload = () => {
    // make sure components are unmounted, redux's listener are unsubscribed
    // to fix issue with iframe navigation in IE
    registeredContainers.forEach((container) => {
        ReactDOM.unmountComponentAtNode(container);
    });
};

const bootstrapComponents = () => {
    if (document.getElementById('miniCart')) {
        renderReact(
            <Provider store={store}>
                <MiniCart />
            </Provider>,
            document.getElementById('miniCart')
        );
    }
    if (document.getElementById('quickSearch')) {
        renderReact(
            <Provider store={store}>
                <QuickSearch />
            </Provider>,
            document.getElementById('quickSearch')
        );
    }
    if (document.getElementById('bnr')) {
        renderReact(
            <Provider store={store}>
                <Bnr />
            </Provider>,
            document.getElementById('bnr')
        );
    }
    if (document.getElementById('navbar')) {
        renderReact(
            <Provider store={store}>
                <Navigation />
            </Provider>,
            document.getElementById('navbar')
        );
    }
    if (document.getElementById('sizeGuide')) {
        renderReact(
            <Provider store={store}>
                <SizeGuide />
            </Provider>,
            document.getElementById('sizeGuide')
        );
    }
    if (document.getElementById('sizeSelector')) {
        renderReact(
            <Provider store={store}>
                <SizeSelector />
            </Provider>,
            document.getElementById('sizeSelector')
        );
    }
    if (document.getElementById('productReviews')) {
        const ProductReviews = DynamicComponent({
            loader: () => import('./Components/ProductReviews'),
        });
        const productReviewsJson = document.getElementById('productReviewsJson');
        const initialModel = productReviewsJson ? JSON.parse(productReviewsJson.textContent || '{}') : {};
        renderReact(
            <Provider store={store}>
                <ProductReviews initialModel={initialModel} />
            </Provider>,
            document.getElementById('productReviews')
        );
    }
    if (document.getElementById('facetedSearch')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearch />
            </Provider>,
            document.getElementById('facetedSearch')
        );
    }
    if (document.getElementById('facetedSearchCompact')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchCompactContainer />
            </Provider>,
            document.getElementById('facetedSearchCompact')
        );
    }
    if (document.getElementById('myPagePersons')) {
        const PersonList = DynamicComponent({
            loader: () => import('./Components/PersonListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <PersonList />
            </Provider>,
            document.getElementById('myPagePersons')
        );
    }
    if (document.getElementById('myPageAddresses')) {
        const AddressList = DynamicComponent({
            loader: () => import('./Components/AddressListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <AddressList />
            </Provider>,
            document.getElementById('myPageAddresses')
        );
    }
    if (document.getElementById('checkout')) {
        const checkoutVariant = window.__litium?.checkoutVariant;
        const Checkout = DynamicComponent({
            loader: () =>
                checkoutVariant === 'v2'
                    ? import('./Components/CheckoutV2')
                    : import('./Components/Checkout'),
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            document.getElementById('checkout')
        );
    }
    if (document.getElementById('oneTimeOrder')) {
        const OneTimeOrder = DynamicComponent({
            loader: () => import('./Components/OneTimeOrder'),
        });
        renderReact(
            <OneTimeOrder initialModel={window.__litium?.preloadState?.oneTimeOrder || {}} />,
            document.getElementById('oneTimeOrder')
        );
    }
    if (document.getElementById('lightBoxImages')) {
        const LightboxImages = DynamicComponent({
            loader: () => import('./Components/LightboxImages'),
        });
        import('./Reducers/LightboxImages.reducer').then(({ lightboxImages }) => {
            store.injectReducer('lightboxImages', lightboxImages);
            const rootElement = document.getElementById('lightBoxImages');
            const images = Array.from(rootElement.querySelectorAll('template')).map((img) => {
                // Parse from inert template markup to avoid browser-specific template fragment quirks.
                const parser = document.createElement('div');
                parser.innerHTML = img.innerHTML;
                const firstImageNode = parser.querySelector('img');

                return {
                    thumbHtml: firstImageNode ? firstImageNode.outerHTML : '',
                    src: img.dataset.src,
                    fullSrc: img.dataset.fullSrc || img.dataset.src,
                    alt: firstImageNode && firstImageNode.getAttribute('alt') ? firstImageNode.getAttribute('alt') : '',
                };
            });
            renderReact(
                <Provider store={store}>
                    <LightboxImages images={images} />
                </Provider>,
                document.getElementById('lightBoxImages')
            );
        });
    }

    if (document.querySelectorAll('.slider').length > 0) {
        const Slider = DynamicComponent({
            loader: () => import('./Components/Slider'),
        });
        Array.from(document.querySelectorAll('.slider')).forEach((slider, index) => {
            const values = [...slider.querySelectorAll('template')].map((block) => {
                return {
                    html: block.innerHTML,
                };
            });
            if (values.length > 0) {
                renderReact(<Slider values={values} />, slider);
            }
        });
    }

    if (document.querySelectorAll('.add-to-wishlist').length > 0) {
        Array.from(document.querySelectorAll('.add-to-wishlist')).forEach((buttons) => {
            const { articleNumber, isInWishlist } = buttons.dataset;
            renderReact(
                <Provider store={store}>
                    <AddToWishlistButton
                        articleNumber={articleNumber}
                        initialIsInWishlist={typeof isInWishlist === 'string' ? isInWishlist.toLowerCase() === 'true' : null}
                    />
                </Provider>, buttons
            );
        });
    }

    if (document.querySelectorAll('.delete-monitored').length > 0) {
        Array.from(document.querySelectorAll('.delete-monitored')).forEach((monitored) => {
            const { articlenumber } = monitored.dataset;
            renderReact(
                <Provider store={store}>
                    <RemoveButton articleNumber={articlenumber} />
                </Provider>, monitored
            );
        });
    }

    if (document.getElementById('showMore')) {
        renderReact(
            <Provider store={store}>
                <ShowMore text={document.getElementById('showMore').dataset.text} />
            </Provider>,
            document.getElementById('showMore')
        );
    }

    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./Components/BuyButton'),
        });
        Array.from(document.querySelectorAll('buy-button')).forEach((button) => {
            console.log(button.dataset);
            const { articleNumber, quantityFieldId, href, cssClass, label, monitorlabel, monitorloginlabel, isresellerlabel } = button.dataset;
            renderReact(
                <Provider store={store}>
                    <BuyButton
                        {...{
                            label,
                            articleNumber,
                            quantityFieldId,
                            href,
                            cssClass,
                            monitorlabel,
                            monitorloginlabel,
                            isresellerlabel,
                        }}
                    />
                </Provider>,
                button
            );
        });
    }

    if (document.getElementById('orderHistoryPage')) {
        const OrderList = DynamicComponent({
            loader: () => import('./Components/OrderHistoryListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <OrderList />
            </Provider>,
            document.getElementById('orderHistoryPage')
        );
    }
    if (document.getElementById('myPageReviews')) {
        const MyPageReviews = DynamicComponent({
            loader: () => import('./Components/MyPageReviews'),
        });
        const myPageReviewsJson = document.getElementById('myPageReviewsJson');
        const initialModel = myPageReviewsJson ? JSON.parse(myPageReviewsJson.textContent || '{}') : {};
        renderReact(
            <Provider store={store}>
                <MyPageReviews initialModel={initialModel} />
            </Provider>,
            document.getElementById('myPageReviews')
        );
    }
    if (document.getElementById('myPagePoints')) {
        const MyPagePoints = DynamicComponent({
            loader: () => import('./Components/MyPagePoints'),
        });
        const myPagePointsJson = document.getElementById('myPagePointsJson');
        const initialModel = myPagePointsJson ? JSON.parse(myPagePointsJson.textContent || '{}') : {};
        renderReact(
            <Provider store={store}>
                <MyPagePoints initialModel={initialModel} />
            </Provider>,
            document.getElementById('myPagePoints')
        );
    }
};

bootstrapComponents();

if (document.readyState === 'loading') {
    window.addEventListener('load', bootstrapNonCriticalEnhancements, { once: true });
} else {
    bootstrapNonCriticalEnhancements();
}


